import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Container,
  Grid2 as Grid,
  Snackbar,
  IconButton,
  InputAdornment,
  Dialog,
  DialogContent,
  Slide,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import MathGEMLogo from "../assets/MATHSGEM.png";
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/system';
import { useAuth } from "../AuthContext";

// Animation for Dialog entrance
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled modern Dialog components
const ModernDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    border: '1px solid rgba(173, 223, 173, 0.3)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 400,
  },
}));

const ModernDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 25,
  padding: theme.spacing(1, 4),
  background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
  color: 'white',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    background: 'linear-gradient(45deg, #45a049 30%, #75b377 90%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
  },
  transition: 'all 0.3s ease-in-out',
}));

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const getFriendlyErrorMessage = (errorCode) => {
    const errorMap = {
      "auth/invalid-email": "The email address you entered is invalid. Please check and try again.",
      "auth/user-disabled": "This account has been disabled. Please contact support for assistance.",
      "auth/user-not-found": "No account exists with this email. Please sign up or check your email.",
      "auth/invalid-credential": "No account exists with these credentials. Please sign up or check your email or password.",
      "auth/wrong-password": "Incorrect password. Please try again or reset your password.",
      "auth/too-many-requests": "Too many attempts. Please try again later or reset your password.",
      "auth/network-request-failed": "Network error. Please check your internet connection and try again.",
      "auth/email-already-verified": "Your email is already verified. Please sign in.",
      default: "An unexpected error occurred. Please try again or contact support.",
    };
    return errorMap[errorCode] || errorMap.default;
  };

  useEffect(() => {
    if (!loading && user && user.emailVerified) {
      console.log("User authenticated:", user.uid, "Role:", user.role);
      checkUserStatus(user);
    } else if (user && !user.emailVerified) {
      signOut(auth).then(() => {
        setOpenVerifyDialog(true);
      }).catch((err) => {
        console.error("Sign-out error:", err);
      });
    }
  }, [user, loading, navigate]);

  const checkUserStatus = async (currentUser) => {
    try {
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const trialEndDate = userData.trialEndDate ? new Date(userData.trialEndDate) : null;
        const now = new Date();

        if (userData.status === "premium") {
          navigate("/dashboard");
        } else if (userData.status === "trial" && trialEndDate && now > trialEndDate) {
          // Trial expired
          navigate("/dashboard", { state: { trialExpired: true } });
        } else {
          // Trial still active
          navigate("/dashboard");
        }
      } else {
        setError("User data not found. Please contact support.");
        setOpenSnackbar(true);
        await signOut(auth);
      }
    } catch (err) {
      console.error("Error checking user status:", err);
      setError("Error checking user status. Please try again.");
      setOpenSnackbar(true);
      await signOut(auth);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    setOpenSnackbar(false);
    setOpenVerifyDialog(false);
    setIsSigningIn(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const currentUser = userCredential.user;

      if (!currentUser.emailVerified) {
        await signOut(auth);
        setOpenVerifyDialog(true);
        setIsSigningIn(false);
        return;
      }

      console.log("Sign-in successful:", currentUser.uid);
      await checkUserStatus(currentUser);
    } catch (err) {
      console.error("Sign-in error:", err.code, err.message);
      const friendlyMessage = getFriendlyErrorMessage(err.code);
      setError(friendlyMessage);
      setOpenSnackbar(true);
      setIsSigningIn(false);
    }
  };

  const handleResendVerificationEmail = async () => {
    if (!email || !password) {
      setError("Please enter your email and password to resend the verification email.");
      setOpenSnackbar(true);
      return;
    }
    if (resendCooldown) {
      setError("Please wait 60 seconds before resending the verification email.");
      setOpenSnackbar(true);
      return;
    }
    setError(null);
    setOpenSnackbar(false);
    setIsResending(true);
    setResendCooldown(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const currentUser = userCredential.user;

      if (currentUser.emailVerified) {
        setError("Your email is already verified. Please sign in.");
        setOpenSnackbar(true);
      } else {
        await sendEmailVerification(currentUser, {
          url: 'https://mathsgemseychelles.com/sign-in',
          handleCodeInApp: false,
        });
        setError("Verification email resent successfully! Check your inbox or spam folder.");
        setOpenSnackbar(true);
      }
      await signOut(auth);
    } catch (err) {
      console.error("Resend verification error:", err.code, err.message);
      const friendlyMessage = getFriendlyErrorMessage(err.code);
      setError(friendlyMessage);
      setOpenSnackbar(true);
    } finally {
      setIsResending(false);
      setTimeout(() => setResendCooldown(false), 60000);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address to reset your password.");
      setOpenSnackbar(true);
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setError(null);
      setOpenSnackbar(false);
      navigate("/reset-password", { state: { success: "Password reset email sent! Check your inbox." } });
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err.code) || "Failed to send reset email. Please try again.";
      setError(friendlyMessage);
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleVerifyDialogClose = async () => {
    setOpenVerifyDialog(false);
    await signOut(auth);
    window.location.reload();
  };

  const AnimatedIcon = styled(ReadMoreIcon)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    transition: 'transform 0.3s ease-in-out',
    '.MuiButton-root:hover &': {
      animation: 'bounce 0.5s infinite',
    },
    '@keyframes bounce': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-5px)' },
    },
  }));

  if (loading) {
    return (
      <Container style={{ marginTop: 100 }}>
        <Typography variant="h5" align="center">
          Loading...
        </Typography>
      </Container>
    );
  }


  return (
    <>
    <Container style={{ marginTop: 100 }}>
      <Paper sx={{ borderRadius: 5, margin: { md: 8, xs: 2 } }} elevation={8}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              sx={{
                maxWidth: 400,
                mx: "auto",
                p: 3,
                borderRadius: 2,
                my: { md: 8, xs: 2 },
              }}
              elevation={0}
            >
              <Typography variant="h4" sx={{ fontWeight: 800 }} align="center" gutterBottom>
                Sign In
              </Typography>
              <Typography variant="subtitle1" align="center" gutterBottom>
                Enter your registered details below to Sign In to the application.
              </Typography>
              <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert
                  onClose={handleCloseSnackbar}
                  severity={error && (error.includes("Failed") || error.includes("Too many") || error.includes("Please") || error.includes("already verified")) ? "error" : "success"}
                  sx={{ width: "100%" }}
                >
                  {error || "Verification email resent successfully!"}
                </Alert>
              </Snackbar>

              <ModernDialog
                open={openVerifyDialog}
                onClose={handleVerifyDialogClose}
                TransitionComponent={Transition}
                aria-labelledby="verify-dialog-title"
              >
                <ModernDialogContent>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#2e7d32',
                      mb: 2,
                    }}
                  >
                    Email Verification Required
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#424242',
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    Please verify your email address before signing in. Check your inbox for a verification email.
                  </Typography>
                  <ModernButton
                    onClick={handleResendVerificationEmail}
                    disabled={isResending || resendCooldown}
                    sx={{ mb: 2 }}
                  >
                    {isResending ? <CircularProgress size={24} color="inherit" /> : "Resend Verification Email"}
                  </ModernButton>
                  <ModernButton onClick={handleVerifyDialogClose}>
                    Understood
                  </ModernButton>
                </ModernDialogContent>
              </ModernDialog>

              <form onSubmit={handleSignIn}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
                <TextField
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  label="Password"
                  fullWidth
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Grid container justifyContent="center" sx={{ mt: 2 }}>
                  <Grid>
                    <Button
                      type="submit"
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderColor: 'green',
                        color: 'green',
                        '&:hover': {
                          borderColor: 'green',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 120,
                      }}
                      disabled={isSigningIn}
                    >
                      {isSigningIn ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <>
                          Sign In
                          <AnimatedIcon />
                        </>
                      )}
                    </Button>
                  </Grid>
                </Grid>
                <br />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  <Link
                    to="#"
                    onClick={handleForgotPassword}
                    style={{ color: "#0064ff", textDecoration: "none", cursor: "pointer" }}
                  >
                    <b>Forgot Password?</b>
                  </Link>
                </Typography>
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  Don't have an account?
                  <Link to="/sign-up" style={{ color: "#0064ff", textDecoration: "none" }}>
                    <b>Register</b>
                  </Link>
                </Typography>
              </form>
            </Paper>
          </Grid>
          <Grid
            sx={{
              paddingTop: 10,
              backgroundColor: "#addfad",
              backgroundImage: "linear-gradient(480deg, #addfad 0%, #fffafa 74%)",
              borderRadius: "0 10px 10px 0px",
              display: { md: 'block', xs: 'none' },
            }}
            size={{ md: 6, xs: 12 }}
          >
            <img style={{ width: 500 }} src={MathGEMLogo} alt="Math Gem Seychelles" />
          </Grid>
        </Grid>
      </Paper>
      <br />
      <br />
      <br />
      <br />
    </Container>
    </>
  );
}

export default SignIn;